<template>
  <b-modal
    id="show-video-modal"
    size="xl"
    @show="onShow"
    @hide="onHide"
  >
    <template v-slot:modal-title>
      {{ trans('video') }}
      <small class="text-muted">
        #{{ video.id }}
      </small>
      <sup v-if="video.publish_to_partner">
        <b-badge variant="primary">
          {{ organization.can_publish && organization.partner == video.publish_to_partner ? trans('published') : video.publish_to_partner }}
        </b-badge>
      </sup>
    </template>
    <b-row>
      <b-col v-if="video" class="text-center">
        <b-aspect aspect="16:9">
          <video
            ref="video"
            :src="video.url"
            :poster="video.thumb_url"
            controls
            class="w-100"
          />
        </b-aspect>
        <p>
          <b-button variant="outline-primary" @click="showClipping = !showClipping" :disabled="busy || disabled || showThumbAt">
            {{ trans('clip_video') }}
            <i :class="`fa fa-fw fa-caret-${showClipping ? 'up' : 'down'}`"></i>
          </b-button>
          <b-button variant="outline-primary" class="ml-2" @click="showThumbAt = !showThumbAt" :disabled="busy || disabled || showClipping">
            {{ trans('thumbnail') }}
            <i :class="`fa fa-fw fa-caret-${showThumbAt ? 'up' : 'down'}`"></i>
          </b-button>
        </p>
        <p v-if="showClipping">
          {{ trans('clip_video_instructions') }}
        </p>
        <p class="lead" v-if="showClipping && currentTime > 0">
          {{ moment().startOf('day').seconds(currentTime).format('H:mm:') }}{{ numeral(currentTime % 60).format('00.000') }}
        </p>
        <p v-if="showClipping">
          <b-button variant="primary" @click="onClip" :disabled="busy">
            {{ trans('apply') }}
          </b-button>
          <b-button variant="light" @click="showClipping = false" :disabled="busy">
            {{ trans('cancel') }}
          </b-button>
        </p>
        <p v-if="showThumbAt">
          {{ trans('thumbnail_instructions') }}
        </p>
        <p class="lead" v-if="showThumbAt && currentTime > 0">
          {{ moment().startOf('day').seconds(currentTime).format('H:mm:') }}{{ numeral(currentTime % 60).format('00.000') }}
        </p>
        <p v-if="showThumbAt">
          <b-button variant="primary" @click="onThumb" :disabled="busy">
            {{ trans('apply') }}
          </b-button>
          <b-button variant="light" @click="showThumbAt = false" :disabled="busy">
            {{ trans('cancel') }}
          </b-button>
        </p>
      </b-col>

      <b-col v-if="video" class="mt-3">
        <b-form-group :label="trans('name')">
          <b-form-input v-model="name" :disabled="busy || disabled" />
        </b-form-group>
        <b-form-group :label="trans('description')">
          <b-form-textarea v-model="description" :disabled="busy || disabled" />
        </b-form-group>
        <b-form-group :label="trans('date_of_recording')">
          <datetime v-model="recorded_at" :config="dateOptions" :disabled="busy || disabled" />
        </b-form-group>
        <b-form-group :label="trans('video_link')">
          <b-input-group class="my-2">
            <b-form-input :value="video.short_url" readonly></b-form-input>
            <b-input-group-append>
              <b-button variant="outline-primary" v-clipboard:copy="video.short_url" :title="trans('copy')" v-b-tooltip.hover class="px-2">
                <i class="fa fa-fw fa-copy"></i>
              </b-button>
              <a :href="video.url" target="_blank" class="btn btn-outline-primary px-2" :title="trans('view')" v-b-tooltip.hover>
                <i class="fa fa-fw fa-external-link-alt"></i>
              </a>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <b-form-group v-if="organization.can_publish" class="mb-0">
          <b-form-checkbox
            v-model="publish_to_partner"
            :value="organization.partner"
            :unchecked-value="null"
            :disabled="busy || disabled"
          >
            {{ trans('published_to_partner').replace(':partner', organization.partner) }}
          </b-form-checkbox>
        </b-form-group>
        <b-row>
          <b-col>
            <b-link :href="`/organization/${this.organization.id}/sendout/create?media_id=${video.id}`" target="_blank" :disabled="busy || disabled">
              <i class="fa fa-fw fa-comment"></i>
              {{ trans('send') }}
              <small><i class="fa fa-fw fa-external-link-alt"></i></small>
            </b-link>
          </b-col>
          <b-col class="text-right">
            <b-link :href="`/organization/${this.organization.id}/media/qr_code/${video.id}`" target="_blank">
              <i class="fa fa-fw fa-qrcode"></i>
              {{ trans('print_qr_code') }}
              <small><i class="fa fa-fw fa-external-link-alt"></i></small>
            </b-link>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>

    <template v-slot:modal-footer>
      <b-row class="w-100">
        <b-col v-if="!publish_to_partner || organization.can_publish" class="col-auto">
          <delete-button variant="outline-danger" :message="trans('media_delete_confirm')" @confirm="onDelete" :disabled="busy">
            {{ trans('delete') }}
          </delete-button>
        </b-col>
        <b-col class="text-right">
          <b-button variant="light" @click="$bvModal.hide('show-video-modal')">
            {{ trans(!disabled ? 'cancel' : 'close') }}
          </b-button>
          <b-button
            v-if="!publish_to_partner || organization.can_publish"
            variant="primary"
            @click="onSave"
            :disabled="busy"
          >
            {{ trans('save') }}
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
  import DeleteButton from './../../../common/components/DeleteConfirmButton'
  import Datetime from 'vue-bootstrap-datetimepicker'

  export default {
    props: {
      organization: Object,
      video: Object,
    },
    components: {
      DeleteButton,
      Datetime,
    },
    data() {
      return {
        trans: trans,
        numeral: numeral,
        moment: moment,
        busy: false,
        error: null,
        name: null,
        description: null,
        recorded_at: null,
        dateOptions: {
          locale: window.lang,
          format: 'LLL',
          showClear: true,
          stepping: 15,
        },
        currentTime: null,
        showClipping: false,
        showThumbAt: false,
        publish_to_partner: null,
      }
    },
    computed: {
      disabled() {
        return this.publish_to_partner && !this.organization.can_publish
      },
    },
    watch: {
      showClipping() {
        if (this.showClipping) {
          this.timerInterval = setInterval(() => {
            this.currentTime = this.$refs.video.currentTime
          }, 200)
        } else {
          clearInterval(this.timerInterval)
        }
      },
      showThumbAt() {
        if (this.showThumbAt) {
          this.timerInterval = setInterval(() => {
            this.currentTime = this.$refs.video.currentTime
          }, 200)
        } else {
          clearInterval(this.timerInterval)
        }
      },
    },
    methods: {
      onClip() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/media/video/${this.video.id}/clip`
        axios.post(url, {
          clip: this.currentTime,
        })
        .then(response => {
          this.busy = false
          this.$bvModal.hide('show-video-modal')
          _.toastSuccess(this, response.data.message)
          this.$emit('saved')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onThumb() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/media/video/${this.video.id}/thumb`
        axios.post(url, {
          thumb_at: this.currentTime,
        })
        .then(response => {
          this.busy = false
          this.$bvModal.hide('show-video-modal')
          _.toastSuccess(this, response.data.message)
          this.$emit('saved')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onSave() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/media/video/${this.video.id}`
        let data = {
          name: this.name,
          description: this.description,
          recorded_at: this.recorded_at ? moment(this.recorded_at).toISOString() : null,
        }
        if (this.organization.can_publish) data.publish_to_partner = this.publish_to_partner
        axios.patch(url, data)
        .then(response => {
          this.busy = false
          this.$emit('saved')
          this.$bvModal.hide('show-video-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onDelete() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/media/video/${this.video.id}`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.$emit('deleted')
          this.$bvModal.hide('show-video-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onHide() {
        this.currentTime = null
        this.showClipping = false
        this.showThumbAt = false
      },
      onShow() {
        if (this.video) {
          this.name = this.video.name
          this.description = this.video.description
          this.recorded_at = this.video.recorded_at ? moment(this.video.recorded_at).format('LLL') : null
          this.publish_to_partner = this.video.publish_to_partner
        } else {
          this.name = null
          this.description = null
          this.recorded_at = null
          this.publish_to_partner = null
        }
      },
    },
    mounted() {
      window.videoModal = this
    },
  }
</script>